import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { motion } from 'framer-motion';
import { FiUsers, FiCheckSquare, FiClock, FiTrendingUp } from 'react-icons/fi';
import { format } from 'date-fns';

const Dashboard = () => {
  const { user, isAdmin } = useAuth();
  const [stats, setStats] = useState({
    teams: 0,
    tasks: 0,
    pendingTasks: 0,
    completedTasks: 0,
  });
  const [recentTasks, setRecentTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [teamsRes, tasksRes] = await Promise.all([
        api.get('/teams'),
        api.get('/tasks'),
      ]);

      const teams = teamsRes.data.teams || [];
      const tasks = tasksRes.data.tasks || [];
      const pendingTasks = tasks.filter(t => t.status === 'pending' || t.status === 'in_progress');
      const completedTasks = tasks.filter(t => t.status === 'completed');

      setStats({
        teams: teams.length,
        tasks: tasks.length,
        pendingTasks: pendingTasks.length,
        completedTasks: completedTasks.length,
      });

      setRecentTasks(tasks.slice(0, 5));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { label: 'Teams', value: stats.teams, icon: FiUsers, color: 'bg-blue-500' },
    { label: 'Total Tasks', value: stats.tasks, icon: FiCheckSquare, color: 'bg-green-500' },
    { label: 'Pending Tasks', value: stats.pendingTasks, icon: FiClock, color: 'bg-yellow-500' },
    { label: 'Completed', value: stats.completedTasks, icon: FiTrendingUp, color: 'bg-purple-500' },
  ];

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-gray-500',
      in_progress: 'bg-blue-500',
      completed: 'bg-green-500',
      blocked: 'bg-red-500',
    };
    return colors[status] || colors.pending;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 lg:space-y-6 animate-fade-in">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-4 lg:mb-6"
      >
        <h1 className="text-2xl lg:text-3xl font-bold text-white mb-1 lg:mb-2">
          Welcome back, {user?.name}! 👋
        </h1>
        <p className="text-white/80 text-sm lg:text-base">
          {isAdmin ? 'Manage your teams and tasks' : 'View your assigned tasks'}
        </p>
      </motion.div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white/10 backdrop-blur-md rounded-xl p-4 lg:p-6 border border-white/20 active:bg-white/15 transition-all touch-manipulation"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-white/70 text-xs lg:text-sm mb-1 truncate">{stat.label}</p>
                  <p className="text-2xl lg:text-3xl font-bold text-white">{stat.value}</p>
                </div>
                <div className={`${stat.color} p-2 lg:p-3 rounded-lg flex-shrink-0 ml-2`}>
                  <Icon className="text-white" size={20} />
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Recent Tasks */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-white/10 backdrop-blur-md rounded-xl p-4 lg:p-6 border border-white/20"
      >
        <div className="flex items-center justify-between mb-3 lg:mb-4">
          <h2 className="text-lg lg:text-xl font-bold text-white">Recent Tasks</h2>
          <Link
            to="/tasks"
            className="text-white/80 hover:text-white text-sm font-medium"
          >
            View all →
          </Link>
        </div>
        {recentTasks.length === 0 ? (
          <p className="text-white/60 text-center py-8">No tasks yet</p>
        ) : (
          <div className="space-y-3">
            {recentTasks.map((task) => (
              <Link
                key={task.id}
                to={`/tasks/${task.id}`}
                className="block bg-white/5 hover:bg-white/10 rounded-lg p-4 transition-all"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-white font-medium mb-1">{task.title}</h3>
                    <p className="text-white/60 text-sm mb-2 line-clamp-2">
                      {task.description || 'No description'}
                    </p>
                    <div className="flex items-center space-x-4 text-xs text-white/50">
                      {task.team_name && (
                        <span className="flex items-center">
                          <FiUsers size={14} className="mr-1" />
                          {task.team_name}
                        </span>
                      )}
                      {task.due_date && (
                        <span className="flex items-center">
                          <FiClock size={14} className="mr-1" />
                          {format(new Date(task.due_date), 'MMM dd, yyyy')}
                        </span>
                      )}
                    </div>
                  </div>
                  <span
                    className={`${getStatusColor(task.status)} text-white text-xs px-2 py-1 rounded-full capitalize`}
                  >
                    {task.status.replace('_', ' ')}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default Dashboard;
